import { useState, useMemo } from 'react';
import { useQueries, useQuery } from '@tanstack/react-query';
import { Card, Descriptions, Dropdown, Modal, Table, Tag } from 'antd';
import { ColumnsType } from 'antd/es/table';

import { ReactComponent as Plus } from '../../../assets/add.svg';
import { ReactComponent as Ellipsis } from '../../../assets/ellipsis.svg';
import Button from '../../../custom/button/button';
import { getRoles, getPrivileges, getPermissions } from '../request';
import { getErrorMessage } from '../../../utils/getErrorMessage';

import AddRoleForm from './addRoleForm';
import EditRoleForm from './editRoleForm';

const AdminRoles = () => {
  const [openAdd, setOpenAdd] = useState(false);
  const [openEdit, setOpenEdit] = useState(false);
  const [openView, setOpenView] = useState(false);
  const [selectedRole, setSelectedRole] = useState<Role | null>(null);

  const { data, isLoading, isError, error, refetch } = useQuery({
    queryKey: ['get-all-roles'],
    queryFn: getRoles,
  });

  const [privilegesQuery, permissionsQuery] = useQueries({
    queries: [
      { queryKey: ['get-all-privileges'], queryFn: getPrivileges },
      { queryKey: ['get-all-permissions'], queryFn: getPermissions },
    ],
  });

  const toggleAddModal = () => setOpenAdd((prev) => !prev);
  const toggleEditModal = () => setOpenEdit((prev) => !prev);
  const toggleViewModal = () => setOpenView((prev) => !prev);

  const handleEdit = (record: Role) => {
    setSelectedRole(record);
    toggleEditModal();
  };

  const handleView = (record: Role) => {
    setSelectedRole(record);
    toggleViewModal();
  };

  const allPrivileges = privilegesQuery?.data?.data as Privilege[];
  const allPermissions = permissionsQuery?.data?.data as Permission[];

  const permissionMap = useMemo(() => {
    return allPermissions?.reduce(
      (acc, permission) => { acc[permission.id] = permission.name; return acc; },
      {} as Record<number, string>,
    );
  }, [allPermissions]);

  const rolePrivileges = useMemo(() => {
    if (!selectedRole || !allPrivileges) return [];
    return allPrivileges.filter((p) => p.roleId === selectedRole.id);
  }, [selectedRole, allPrivileges]);

  const columns: ColumnsType<Role> = [
    {
      key: 'S/N',
      title: 'S/N',
      render: (_text, _record, index) => index + 1,
    },
    {
      key: 'name',
      title: 'Role Name',
      dataIndex: 'name',
    },
    {
      key: 'action',
      title: 'Action',
      render: (_, record) => (
        <Dropdown
          menu={{
            items: [
              {
                label: 'View Privileges',
                key: 'view',
                onClick: () => handleView(record),
              },
              {
                label: 'Edit Role',
                key: 'edit',
                onClick: () => handleEdit(record),
              },
            ],
          }}
          trigger={['click']}
        >
          <Ellipsis />
        </Dropdown>
      ),
    },
  ];

  return (
    <div>
      <section className="space-between">
        <h3>Admin Roles</h3>
        <Button text="Add Role" iconBefore={<Plus />} onClick={toggleAddModal} />
      </section>

      <br />

      <Card bordered={false}>
        <Table
          dataSource={data}
          columns={columns}
          rowKey={(record) => record.id}
          scroll={{ x: true }}
          loading={isLoading}
          pagination={false}
          locale={{ emptyText: isError ? getErrorMessage(error) : 'No roles found' }}
        />
      </Card>

      <Modal title="Add Role" open={openAdd} onCancel={toggleAddModal} footer={null}>
        <AddRoleForm handleClose={toggleAddModal} refetch={refetch} />
      </Modal>

      <Modal title="Edit Role" open={openEdit} onCancel={toggleEditModal} footer={null}>
        {selectedRole && (
          <EditRoleForm role={selectedRole} handleClose={toggleEditModal} refetch={refetch} />
        )}
      </Modal>

      <Modal
        title={`Privileges — ${selectedRole?.name}`}
        open={openView}
        onCancel={toggleViewModal}
        footer={null}
        width={600}
      >
        {selectedRole && (
          <>
            {rolePrivileges.length === 0 ? (
              <p style={{ color: '#999', textAlign: 'center', padding: '24px 0' }}>
                No privileges assigned to this role.
              </p>
            ) : (
              rolePrivileges.map((privilege) => (
                <Descriptions
                  key={privilege.id}
                  column={1}
                  bordered
                  size="small"
                  style={{ marginBottom: 16 }}
                >
                  <Descriptions.Item label="Privilege ID">{privilege.id}</Descriptions.Item>
                  <Descriptions.Item label="Permissions">
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                      {privilege.permissionId?.length ? (
                        privilege.permissionId.map((id) => (
                          <Tag key={id}>{permissionMap?.[id] || id}</Tag>
                        ))
                      ) : (
                        <span style={{ color: '#999' }}>None</span>
                      )}
                    </div>
                  </Descriptions.Item>
                  <Descriptions.Item label="Institution">
                    {privilege.institutionShortName}
                  </Descriptions.Item>
                  <Descriptions.Item label="Created Date">
                    {new Date(privilege.createdDate).toLocaleDateString()}
                  </Descriptions.Item>
                </Descriptions>
              ))
            )}
          </>
        )}
      </Modal>
    </div>
  );
};

export default AdminRoles;
